/**
 * F-126 BGM UI state: whether the stems panel is open, and which section's
 * stem slot the add/replace dialog is editing. Local view state only — every
 * stem write is a doc mutation the layer performs directly.
 *
 * Module SINGLETON for the same reason `sectionsUi` is one: the panel is
 * opened from the BGM chip in the board's top bar (BoardScreen), which sits
 * outside the canvas tree the panel and the in-camera slot layer mount in,
 * and both mounts read the same state. Stems themselves are never added from
 * the top bar — the slot chips in the BGM column's cells are the only way in.
 */
import { createStore, type StoreApi } from "zustand/vanilla";

/** The board/sheet whose BGM track the panel is showing. */
export interface BgmTarget {
  boardId: string;
  sheetId: string;
}

/** The slot the dialog is editing. */
export interface BgmEditTarget {
  sheetId: string;
  /** The run's TOP row — where the stems are stored. */
  rowId: string;
  colId: string;
  /** The run's label (its mood; a section title on an older board), for the dialog's heading. */
  label: string;
}

export interface BgmUiState {
  target: BgmTarget | null;
  /** Null when no dialog is open. */
  editing: BgmEditTarget | null;
  open(target: BgmTarget): void;
  close(): void;
  edit(target: BgmEditTarget): void;
  closeEditor(): void;
}

export type BgmUiStore = StoreApi<BgmUiState>;

export const bgmUi: BgmUiStore = createStore<BgmUiState>((set) => ({
  target: null,
  editing: null,
  open: (target) => set({ target, editing: null }),
  close: () => set({ target: null, editing: null }),
  edit: (editing) => set({ editing }),
  closeEditor: () => set({ editing: null }),
}));
